import { Link } from "react-router-dom";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import ArticleCard from "@/components/feature/ArticleCard";
import {
  allCategories,
  allTopics,
  getLatestArticles,
  getPopularArticles,
} from "@/utils/data";

export default function Home() {
  const latest = getLatestArticles(6);
  const popular = getPopularArticles(5);

  return (
    <div className="w-full">
      <HeroCarousel topics={allTopics} />

      {/* 分类快捷入口 */}
      <section className="w-full max-w-[1200px] mx-auto px-4 md:px-8 py-12 md:py-16">
        <div className="flex items-end justify-between mb-6 md:mb-8">
          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">探索资讯分类</h2>
            <p className="mt-2 text-sm text-foreground-600">从科学认知到自我关怀，找到你此刻最需要的内容。</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {allCategories.map((category) => (
            <Link
              key={category.id}
              to={`/category/${category.id}`}
              className="group p-5 md:p-6 rounded-lg bg-background-100 border border-background-200 hover:border-primary-300 hover:bg-background-50 transition-colors"
            >
              <span className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 text-2xl group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                <i className={category.icon}></i>
              </span>
              <h3 className="mt-4 text-lg font-medium text-foreground-900 group-hover:text-primary-700 transition-colors">
                {category.name}
              </h3>
              <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2">{category.description}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm text-primary-600">
                浏览分类
                <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* 最新内容 + 热门排行 */}
      <section className="w-full bg-background-100 py-12 md:py-16">
        <div className="w-full max-w-[1200px] mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
            <div className="lg:col-span-2">
              <div className="flex items-end justify-between mb-6">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">最新内容</h2>
                  <p className="mt-2 text-sm text-foreground-600">按发布时间排序，持续更新的健康生活资讯。</p>
                </div>
                <Link to="/category/science" className="hidden sm:inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 whitespace-nowrap">
                  全部资讯
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
                {latest.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            </div>

            <aside className="lg:col-span-1">
              <div className="sticky top-24">
                <div className="flex items-center gap-2 mb-5">
                  <i className="ri-fire-line text-accent-500 text-xl"></i>
                  <h2 className="font-heading text-xl font-bold text-foreground-950">热门推荐</h2>
                </div>
                <div className="flex flex-col gap-3">
                  {popular.map((article, index) => (
                    <ArticleCard key={article.id} article={article} variant="horizontal" rank={index + 1} />
                  ))}
                </div>

                <div className="mt-8 rounded-lg bg-primary-950 p-5 md:p-6">
                  <h3 className="font-heading text-lg font-bold text-background-50">精选专题</h3>
                  <p className="mt-2 text-sm text-primary-100/80">成组阅读，系统了解一个主题。</p>
                  <ul className="mt-4 space-y-2.5">
                    {allTopics.map((topic) => (
                      <li key={topic.id}>
                        <Link
                          to={`/topic/${topic.id}`}
                          className="flex items-center justify-between gap-3 text-sm text-background-100/90 hover:text-background-50 transition-colors"
                        >
                          <span className="line-clamp-1">{topic.title}</span>
                          <i className="ri-arrow-right-s-line shrink-0"></i>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* 网站简介 */}
      <section className="w-full max-w-[1200px] mx-auto px-4 md:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-2 text-sm text-primary-600 mb-4">
              <i className="ri-book-open-line"></i>
              关于本站
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 leading-snug">
              用科学、温和的方式，陪伴你的每一次自我关怀
            </h2>
            <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">
              自慰网站是一个面向成年人的健康生活资讯平台。我们不制造焦虑，也不回避话题，而是以循证的知识和友善的表达，帮助你科学地了解身体与心理。
            </p>
            <p className="mt-3 text-sm md:text-base text-foreground-700 leading-relaxed">
              从性健康科普到情绪调节，从身体照护到生活习惯，我们希望成为你获取可靠信息时，一个值得信赖的地方。
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/about"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                了解更多
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-background-100 text-foreground-800 text-sm border border-background-200 hover:bg-background-200 transition-colors whitespace-nowrap"
              >
                联系反馈
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-lg bg-primary-50 p-5 md:p-6">
              <i className="ri-file-list-3-line text-2xl text-primary-600"></i>
              <p className="mt-3 font-heading text-2xl md:text-3xl font-bold text-foreground-950">40+</p>
              <p className="mt-1 text-sm text-foreground-600">科普资讯</p>
            </div>
            <div className="rounded-lg bg-accent-50 p-5 md:p-6 mt-6">
              <i className="ri-stack-line text-2xl text-accent-600"></i>
              <p className="mt-3 font-heading text-2xl md:text-3xl font-bold text-foreground-950">4</p>
              <p className="mt-1 text-sm text-foreground-600">精选专题</p>
            </div>
            <div className="rounded-lg bg-secondary-50 p-5 md:p-6">
              <i className="ri-heart-3-line text-2xl text-secondary-600"></i>
              <p className="mt-3 font-heading text-2xl md:text-3xl font-bold text-foreground-950">18+</p>
              <p className="mt-1 text-sm text-foreground-600">面向成年读者</p>
            </div>
            <div className="rounded-lg bg-background-100 p-5 md:p-6 mt-6">
              <i className="ri-shield-star-line text-2xl text-primary-600"></i>
              <p className="mt-3 font-heading text-lg md:text-xl font-bold text-foreground-950">循证科普</p>
              <p className="mt-1 text-sm text-foreground-600">不制造焦虑</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}